import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { ChipChange } from '@components/design/migrated/StackedChart/types';
import { ColumnConfig, SortFieldDisplayProps, SortingOptionsType } from '@components/design/migrated/Table';
import { SeriesResp, StackedChartResp } from './Types';

import { SegmentByChartPickers } from '@components/chartpickers/SegmentByChartPickers';
import { getPerformanceAnalysisboardColumns } from '@components/performance-analysis-board/performance-analysis-table-columns/PerformanceAnalysisTableColumns';
import {
  ColumnHeaderForPA,
  DefaultSortingOptionsForPerformanceAnalysisboard,
  getDisplayNameFromHeaderKey,
  PerformanceAnalysisboardTableId,
} from '@models/PerformanceAnalysisboard';
import { renderLoading } from '@pages/Loading';
import usePersistedSorting from '../../utils/hook/sorting/UsePersistedSorting';
import { FactRevenue, FeatureOption, Filters, ShareType, SubSection } from '@utils/pageConfig/Types';

import { SupportsChartExport } from '@ecosystem/export-utils';
import { FeatureSelectorType } from '@components/feature-selector/Types';
import { useInViewTrackingForPerformanceAnalysis } from '@components/performance-analysis-card/PerformanceAnalysisCardHooks';
import { PerformanceAnalysisView } from '@components/performance-analysis-card/Types';
import { KpiDataResp } from '@models/CommonResp';
import { NoResult } from '@pages/NoResult';
import { NO_OPTIONS } from '@utils/StringConstants';
import {
  constructCurrencyLabel,
  formatCurrencyLabelColumnConfigList,
  formatLabelIntoRevenuePriceTitle,
} from '@utils/currencyUtils/CurrencyUtils';
import usePersistedSortingPerformanceAnalysisStackedChart from '../../utils/hook/sorting/UsePersistedSortingPerformanceAnalysisStackedChart';
import { useMultiStackedChartExport } from '@hooks/export/export-stacked-chart/context/MultiStackedChartExportContext';
import { modifiedPAStackChartGrandTotalToTotalValue } from '@utils/TotalUtils';
import { ViewType } from '@hooks/export/Model';
import { TableName } from '@utils/page-analytics/model/TableClickTracking';
import ListBoxSelector from '@components/button-menu/ListBoxSelector';
import { ExploreTabView } from '@components/tab-nav/TabConfig';
import ExploreHeaderIcon from '@pages/explore/header-icon/ExploreHeaderIcon';
import { CreateViewStateFn, ExportFormat } from '@ecosystem/newron-client-lib';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { PerformanceAnalysisStackedBarChartInsight } from '@components/collections/performance-analysis-stacked-bar-chart/Model';
import { containers } from '@ecosystem/newron-design-system';
import { SegmentsStackedBarChart } from '@utils/exploreUtils/ExploreType';

import { LimitPgInfo } from '@pages/LimitPgInfo';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { convertToExploreTrendsStack } from '@data-visualization/stacked-chart/StackedChartUtils';
import ExploreTrendsStackChart from '@data-visualization/trends-stack-chart/ExploreTrendsStackChart';

export interface StackedChartProps {
  stackedChartRespData: StackedChartResp[];
  showPerformanceWithin: FeatureOption;
  isLoading: boolean;
  segmentByFeature: FeatureOption;
  selectedGrowthType: GrowthTypes;
  setSegmentByFeature: (f: FeatureOption) => void;
  featureFilters: Filters[];
  factRevenue: FactRevenue;
  featureSelectorType: FeatureSelectorType;
  currentSortingOption: SortingOptions;
  setCurrentSortingOption: Dispatch<SetStateAction<SortingOptions>>;
  featureHandler: () => void;
  onKpiSelectHandler?: () => void;
  segmentByHandler: (feat: FeatureOption) => void;
  isExportModalOpen?: boolean;
  noResultHandler: () => void;
  volume: string;
  view: ViewType;
  isRestoreFromSavedView: boolean;
  setIsRestoreFromSavedView: (isRestoreFromSavedView: boolean) => void;
  sortByStackedChart: SortFieldDisplayProps;
  setSortByStackedChart: (sortByStackedChart: SortFieldDisplayProps) => void;
  noPeriodExistForMulticountry?: boolean;
  isUsingCollection?: boolean;
  collectionStackedChartHeaderKey?: string;
  isExplorePage?: boolean;
  onChartDownload?: (exportFormats: ExportFormat[]) => Promise<void>;
  isExportButtonDisabled?: boolean;
  setIsExportButtonDisabled?: (setIsExportButtonDisabled: boolean) => void;
  createStackedChartInsightDataFn?: CreateInsightDataFn<PerformanceAnalysisStackedBarChartInsight>;
  isError?: boolean;
  isLimitPG?: boolean;
  componentName: string;
  createViewStateFn?: CreateViewStateFn<any>;
  selectedKpi?: string;
  subSectionForAmplitudeTracking: SubSection;
}

/**
 * Maps header key to applicable / relevant header key for stacked chart. Attempts smart mapping.
 *
 * Defaults to "yourRevenue" if inapplicable header key (marketPrice/yourPrice).
 * @param kpiColumnHeader header key to map
 * @returns mapped header key
 */
const mapStackedChartHeaderKey = (kpiColumnHeader: ColumnHeaderForPA | string): ColumnHeaderForPA => {
  switch (kpiColumnHeader) {
    case ColumnHeaderForPA.marketRevenue:
    case ColumnHeaderForPA.marketVolume:
      return kpiColumnHeader;
    case ColumnHeaderForPA.marketRetailerRevenueShare:
      return ColumnHeaderForPA.marketRetailerRevenueShare;
    case ColumnHeaderForPA.marketRetailerVolumeShare:
      return ColumnHeaderForPA.marketRetailerVolumeShare;
    case ColumnHeaderForPA.yourRevenueShare:
    case ColumnHeaderForPA.yourRevenueMix:
      return ColumnHeaderForPA.yourRevenue;
    case ColumnHeaderForPA.yourVolumeShare:
    case ColumnHeaderForPA.yourVolumeMix:
    case ColumnHeaderForPA.yourVolume:
      return ColumnHeaderForPA.yourVolume;
    case ColumnHeaderForPA.marketExcludeRetailRevenue:
      return ColumnHeaderForPA.marketExcludeRetailRevenue;
    case ColumnHeaderForPA.marketExcludeRetailVolume:
      return ColumnHeaderForPA.marketExcludeRetailVolume;
    default:
      return ColumnHeaderForPA.yourRevenue;
  }
};

/**
 * Maps header key to corresponding kpi of applicable / relevant header key for calling stacked chart API.
 *
 * Defaults to "My Revenue" if inapplicable header key (marketPrice/yourPrice).
 * @param headerKeyToSearch header key to map
 * @param kpiDropdownOptions hard coded object mapping
 * @returns mapped header key
 */
export const mapStackedChartKPI = (headerKeyToSearch: string, kpiDropdownOptions: any) => {
  return kpiDropdownOptions.find(
    (e: { headerKey: any }) => e.headerKey === mapStackedChartHeaderKey(headerKeyToSearch),
  );
};

const invalidKpiHeaderKeys: string[] = [
  ColumnHeaderForPA.yourRevenueMix,
  ColumnHeaderForPA.yourRevenueShare,
  ColumnHeaderForPA.yourVolumeShare,
  ColumnHeaderForPA.yourVolumeMix,
  ColumnHeaderForPA.marketPrice,
  ColumnHeaderForPA.yourPrice,
];

const getChipChange = (value: number): ChipChange => {
  if (value > 0) {
    return ChipChange.up;
  }
  if (value === 0) {
    return ChipChange.neutral;
  }
  return ChipChange.down;
};

/**
 * Returns 0 if negative value, so Stacked chart does not show it.
 * @param series Series data
 * @returns Handled negative series KPI data
 */
export const handleNegativeValuesForSeriesKpi = (series: SeriesResp): KpiDataResp[] => {
  return series.data.map((seriesKpi) => {
    return {
      label: seriesKpi.label,
      value: seriesKpi.value > 0 ? seriesKpi.value : 0,
    };
  });
};

const StackedChart: React.FC<SupportsChartExport & StackedChartProps> = ({
  stackedChartRespData,
  showPerformanceWithin,
  isLoading,
  segmentByFeature,
  setSegmentByFeature,
  selectedGrowthType,
  featureFilters,
  factRevenue,
  featureSelectorType,
  featureHandler,
  currentSortingOption,
  setCurrentSortingOption,
  onKpiSelectHandler,
  segmentByHandler,
  onExportReady,
  isExportModalOpen = true,
  noResultHandler,
  volume,
  view,
  isRestoreFromSavedView,
  setIsRestoreFromSavedView,
  sortByStackedChart,
  setSortByStackedChart,
  noPeriodExistForMulticountry,
  isUsingCollection = false,
  collectionStackedChartHeaderKey,
  isExplorePage = false,
  onChartDownload,
  isExportButtonDisabled = true,
  setIsExportButtonDisabled,
  createStackedChartInsightDataFn,
  isError = false,
  isLimitPG = false,
  componentName,
  selectedKpi,
  subSectionForAmplitudeTracking,
  createViewStateFn,
}) => {
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const { sorting, updateOptions } = usePersistedSorting({
    tableId: PerformanceAnalysisboardTableId,
    defaultSorting: DefaultSortingOptionsForPerformanceAnalysisboard,
  });
  const { stackedChartSorting, updateStackedChartSortingOptions } = usePersistedSortingPerformanceAnalysisStackedChart({
    tableId: PerformanceAnalysisboardTableId,
    defaultSorting: DefaultSortingOptionsForPerformanceAnalysisboard,
  });
  const currencyLabel = constructCurrencyLabel(factRevenue);
  const mappedSorting: SortingOptionsType = mapKpiInSortingOptionsType(stackedChartSorting);
  const mappedHeaderKey: string = collectionStackedChartHeaderKey ?? mappedSorting.headerKey;
  const displayNameHeaderKey: string = formatLabelIntoRevenuePriceTitle(
    getDisplayNameFromHeaderKey(mappedHeaderKey, volume),
    currencyLabel,
  );

  const modifiedStackRespData = modifiedPAStackChartGrandTotalToTotalValue(stackedChartRespData);

  const hasData: boolean =
    !isShowPerfWithinSameWithByFilter(showPerformanceWithin.value, segmentByFeature) &&
    !isNoOptions(showPerformanceWithin.value) &&
    modifiedStackRespData.length > 0;

  const { ref } = useInViewTrackingForPerformanceAnalysis(
    PerformanceAnalysisView.StackedBarChart,
    `${displayNameHeaderKey}`,
    hasData,
    componentName,
    subSectionForAmplitudeTracking,
  );
  const { setChartRefsIsInitialisedStatus } = useMultiStackedChartExport();
  useEffect(() => {
    if (isUsingCollection) {
      setChartRefsIsInitialisedStatus([true]);
    }
  }, []);

  useEffect(() => {
    const cols: ColumnConfig[] = getPerformanceAnalysisboardColumns(ShareType.MarketShare, volume);
    const validCols = cols.filter((col) => !invalidKpiHeaderKeys.includes(col.headerKey));
    setColumns(formatCurrencyLabelColumnConfigList(validCols, factRevenue));
  }, [stackedChartRespData, sorting]);

  /**
   * update the sorting based on other tab when navigating from other tab
   * (e.g. Table, Line, Double Bar chart)
   */
  useEffect(() => {
    // Checking whether the sorting is restored from saved/copy view
    if (!isRestoreFromSavedView) {
      updateStackedChartSortingOptions({
        ...currentSortingOption,
      });
    }
    // Reset the indicator to default
    setIsRestoreFromSavedView(false);
  }, [view]);

  useEffect(() => {
    if (
      ![ColumnHeaderForPA.marketRetailerRevenueShare, ColumnHeaderForPA.marketRetailerVolumeShare].includes(
        mappedSorting.headerKey as ColumnHeaderForPA,
      )
    ) {
      if (setCurrentSortingOption) setCurrentSortingOption(mappedSorting);
    }
  }, [mappedSorting]);

  const chartPicker = () => {
    return (
      <div
        key={`${isLoading}-${segmentByFeature}`} //NOSONAR typescript:S6551
      >
        <SegmentByChartPickers
          isLoading={isLoading}
          label={'By'}
          sorting={mappedSorting}
          stackedChartSorting={stackedChartSorting}
          updateSorting={updateOptions}
          updateStackChartSorting={updateStackedChartSortingOptions}
          kpisConfig={columns}
          segmentByFeature={segmentByFeature}
          setSegmentByFeature={setSegmentByFeature}
          featureFilters={featureFilters}
          showPerformanceWithin={showPerformanceWithin}
          featureSelectorType={featureSelectorType}
          featureHandler={featureHandler}
          onKpiSelectHandler={onKpiSelectHandler}
          segmentByHandler={segmentByHandler}
          currencyLabel={currencyLabel}
          volume={volume}
          sortByStackedChart={sortByStackedChart}
          setSortByStackedChart={setSortByStackedChart}
        />
      </div>
    );
  };

  const { Card } = containers;

  const exploreChartPicker = () => {
    return (
      <div data-testid={'stacked-chart-selector'}>
        <Card.Title title={''}>
          <ListBoxSelector
            isDisabled={isLoading}
            chartName={TableName.EXPLORE_SEGMENTS}
            getDisplayName={getDisplayNameFromHeaderKey}
            factRevenue={factRevenue}
            sorting={mappedSorting}
            stackedChartSorting={stackedChartSorting}
            kpisConfig={columns}
            updateSorting={updateOptions}
            updateStackChartSorting={updateStackedChartSortingOptions}
            featureFilters={featureFilters}
            showPerformanceWithin={showPerformanceWithin}
            segmentByFeature={segmentByFeature}
            setSegmentByFeature={setSegmentByFeature}
            featureHandler={featureHandler}
            segmentByHandler={segmentByHandler}
            featureSelectorType={featureSelectorType}
            onKpiSelectHandler={onKpiSelectHandler}
            volume={volume}
            sortByStackedChart={sortByStackedChart}
            setSortByStackedChart={setSortByStackedChart}
            isStackedChart={true}
            tabView={ExploreTabView.Segments}
            viewType={ViewType.StackedChart}
            selectedKpi={selectedKpi}
          />
          <Card.Title.Actions>
            {onChartDownload && createStackedChartInsightDataFn && (
              <ExploreHeaderIcon
                onChartDownload={onChartDownload}
                isExportButtonDisabled={isExportButtonDisabled}
                volume={volume}
                createInsightDataFn={createStackedChartInsightDataFn}
                exploreChartView={'Segments Stacked Chart' as SegmentsStackedBarChart}
                exploreTabView={ExploreTabView.Segments}
                createViewStateFn={createViewStateFn}
              />
            )}
          </Card.Title.Actions>
        </Card.Title>
      </div>
    );
  };

  const noResult = (
    <>
      {!isUsingCollection && (isExplorePage ? exploreChartPicker() : chartPicker())}
      {isLoading ? renderLoading() : <div ref={ref}>{NoResult()}</div>}
    </>
  );

  const isEmptyData = () => {
    return noPeriodExistForMulticountry || !hasData || isError; //NOSONAR typescript:S6606
  };

  useEffect(() => {
    if (isEmptyData()) {
      noResultHandler();
    } else {
      setIsExportButtonDisabled?.(false);
    }
  }, [stackedChartRespData]);

  if (isLimitPG) {
    return (
      <>
        {!isUsingCollection && (isExplorePage ? exploreChartPicker() : chartPicker())}
        {isLoading ? renderLoading() : <div ref={ref}>{LimitPgInfo()}</div>}
      </>
    );
  }

  if (isEmptyData()) {
    return noResult;
  }

  const stackedChartData = convertToExploreTrendsStack({
    StackChartData: stackedChartRespData,
    selectedKpi,
    selectedGrowthType,
  });

  try {
    const renderChart = isLoading ? (
      renderLoading()
    ) : (
      <div key={`${selectedGrowthType}-${selectedKpi}`}>
        <ExploreTrendsStackChart
          data={stackedChartData}
          selectedGrowthType={selectedGrowthType}
          selectedAbsoluteType={ShareType.MarketShare}
          selectedSaleType={selectedKpi}
          stackedChartSortingOrder={sortByStackedChart}
          volume={volume}
          isMarket={false}
          isSegment={true}
        />
      </div>
    );

    return (
      <>
        {!isUsingCollection && (isExplorePage ? exploreChartPicker() : chartPicker())}
        {renderChart}
      </>
    );
  } catch (err: any) {
    console.error('ExploreTrendsStackChart rendering failed', err);
    return noResult;
  }
};

/**
 * Determine whether kpi (headerKey) inside sorting object requires mapping.
 *
 * @param sorting sorting object
 * @returns mapped kpi sorting object
 */
const mapKpiInSortingOptionsType = (sorting: SortingOptionsType): SortingOptionsType => {
  return invalidKpiHeaderKeys.includes(sorting.headerKey)
    ? {
        ...sorting,
        headerKey: mapStackedChartHeaderKey(sorting.headerKey as ColumnHeaderForPA),
      }
    : sorting;
};

const isShowPerfWithinSameWithByFilter = (showPerformanceWithin: string, segmentByFeature: FeatureOption): boolean => {
  return showPerformanceWithin === segmentByFeature?.value;
};

const isNoOptions = (showPerformanceWithin: string): boolean => {
  return showPerformanceWithin === NO_OPTIONS;
};

export const exportTesting = {
  mapKpiInSortingOptionsType,
};

export default StackedChart;
